import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { pushChannelListingStock } from '../channels/channelsApi'
import { Button, Field, Icon, I, Pill, MOBILE_SINGLE_COLUMN, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import type { SourceAuthority } from '../source/types'
import { getProductStock, updateProductStock } from '../stock/stockApi'
import type { ProductStockRow } from '../stock/types'
import { OptionLinkHint } from './OptionLinkHint'
import { optionListing, optionSyncState, type OptionSyncState } from './productOptions'
import { getProductOptions, updateProductOption } from './productsApi'
import type { ProductOption, ProductOptions, ProductStatus } from './types'

const EYEBROW = { fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)' } as const
const TABLE_HEADER = { padding: '10px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' } as const
type SessionPush = { kind: 'queued' } | { kind: 'failed'; observed: number | null }

function stockErrorKey(error: unknown): string {
  if (error instanceof ApiClientError) return `pages.productDetail.stock.errors.${error.code}`
  return 'pages.productDetail.stock.errors.generic'
}

function SyncCell({ sync, session, option, locale }: { sync: OptionSyncState | null; session: SessionPush | undefined; option: ProductOption; locale: string }) {
  const { t } = useTranslation()
  if (option.status === 'ACTIVE' && session?.kind === 'queued') {
    return (
      <div style={{ display: 'grid', gap: 2, justifyItems: 'start' }}>
        <Pill tone="warning" dot size="sm">{t('pages.products.options.sync.pushing', { quantity: option.availableQty.toLocaleString(locale) })}</Pill>
        <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{t('pages.products.options.sync.startedNow')}</span>
      </div>
    )
  }
  if (option.status === 'ACTIVE' && session?.kind === 'failed') {
    return (
      <div style={{ display: 'grid', gap: 2, justifyItems: 'start' }}>
        <Pill tone="danger" dot size="sm">{t('pages.products.options.sync.pushFailed')}</Pill>
        {session.observed != null && <span style={{ fontSize: 11.5, color: 'var(--danger-700)' }}>{t('pages.products.options.sync.stillShows', { quantity: session.observed.toLocaleString(locale) })}</span>}
      </div>
    )
  }
  if (!sync) return <span style={{ color: 'var(--fg-subtle)', fontWeight: 700 }}>—</span>
  return (
    <div style={{ display: 'grid', gap: 2, justifyItems: 'start' }}>
      <Pill tone={sync.tone} dot size="sm">{sync.label}</Pill>
      {sync.detail && <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{sync.detail}</span>}
    </div>
  )
}

interface ProductOptionsPanelProps {
  accessToken: string
  productId: number
  stockSource: SourceAuthority | null
  catalogSource: SourceAuthority | null
  /** Fired once options load, so the page can fall back to the plain stock panel when there are none. */
  onLoaded?: (options: ProductOptions) => void
}

/** Stock per option for a parent whose Salla options have no SKU; replaces "Stock by warehouse". */
export function ProductOptionsPanel({ accessToken, productId, stockSource, catalogSource, onLoaded }: ProductOptionsPanelProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const locale = i18n.language
  const editable = stockSource?.ownerType === 'TLAAQ'
  const statusEditable = catalogSource?.ownerType === 'TLAAQ'
  const sourceName = stockSource ? (stockSource.ownerType === 'TLAAQ' ? t('pages.products.options.tlaaq') : stockSource.provider ?? stockSource.ownerType) : null
  const [data, setData] = useState<ProductOptions | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [reload, setReload] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  const [draftStatus, setDraftStatus] = useState<ProductStatus>('DRAFT')
  const [saving, setSaving] = useState(false)
  const [rowError, setRowError] = useState<unknown>(null)
  const [sessionPush, setSessionPush] = useState<Record<number, SessionPush>>({})
  const [parentRows, setParentRows] = useState<ProductStockRow[]>([])
  const [clearingParent, setClearingParent] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Fetch-on-deps: show loading again when the product changes or after a save.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setLoadError(null)
    getProductOptions(accessToken, productId)
      .then(async response => {
        const ownRows = response.parentHasOwnStock
          ? (await getProductStock(accessToken, productId)).rows.filter(row => row.onHandQty > 0)
          : []
        if (cancelled) return
        setData(response)
        setParentRows(ownRows)
        onLoaded?.(response)
      })
      .catch(caught => { if (!cancelled) setLoadError(caught) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [accessToken, productId, reload, onLoaded])

  const options = data?.options ?? []
  const warehouses = options[0]?.stock ?? []

  const beginEdit = (option: ProductOption) => {
    setEditingId(option.id)
    setDraftStatus(option.status)
    setDrafts(Object.fromEntries(option.stock.map(row => [row.warehouseId, String(row.onHandQty)])))
    setRowError(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setDrafts({})
    setRowError(null)
  }

  const saveEdit = async (option: ProductOption) => {
    const changes: Array<{ warehouseId: number; onHandQty: number; version: number | null }> = []
    for (const row of editable ? option.stock : []) {
      const draft = (drafts[row.warehouseId] ?? '').trim()
      if (!/^\d+$/.test(draft) || Number(draft) > 2147483647) {
        setRowError(new ApiClientError(400, { code: 'PRODUCT_STOCK_INVALID_QUANTITY' }))
        return
      }
      if (Number(draft) !== row.onHandQty) changes.push({ warehouseId: row.warehouseId, onHandQty: Number(draft), version: row.version })
    }
    const statusChanged = statusEditable && draftStatus !== option.status
    if (changes.length === 0 && !statusChanged) { cancelEdit(); return }
    setSaving(true)
    setRowError(null)
    try {
      const response = await updateProductOption(accessToken, productId, option.id, {
        ...(statusChanged ? { status: draftStatus } : {}), stocks: changes,
      })
      setData(response)
      onLoaded?.(response)
      const nextStatus = statusChanged ? draftStatus : option.status
      setSessionPush(prev => {
        const next = { ...prev }
        delete next[option.id]
        if (editable && nextStatus === 'ACTIVE') next[option.id] = { kind: 'queued' }
        return next
      })
      cancelEdit()
    } catch (caught) {
      setRowError(caught)
      if (caught instanceof ApiClientError && caught.code === 'PRODUCT_STOCK_VERSION_CONFLICT') {
        setReload(value => value + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const retryPush = async (option: ProductOption) => {
    const listing = optionListing(option)
    if (!listing) return
    setRowError(null)
    setSessionPush(prev => ({ ...prev, [option.id]: { kind: 'queued' } }))
    try {
      const result = await pushChannelListingStock(accessToken, option.id, listing.id, { availableQuantity: option.availableQty })
      if (result.failed > 0 || result.pushed === 0) {
        setSessionPush(prev => ({ ...prev, [option.id]: { kind: 'failed', observed: listing.finiteStockQuantity ?? null } }))
      } else {
        setReload(value => value + 1)
      }
    } catch {
      setSessionPush(prev => ({ ...prev, [option.id]: { kind: 'failed', observed: listing.finiteStockQuantity ?? null } }))
    }
  }

  const clearParentStock = async () => {
    setClearingParent(true)
    setRowError(null)
    try {
      for (const row of parentRows) {
        await updateProductStock(accessToken, productId, row.warehouseId, { onHandQty: 0, version: row.version })
      }
      setReload(value => value + 1)
    } catch (caught) {
      setRowError(caught)
    } finally {
      setClearingParent(false)
    }
  }

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'baseline', gap: 12, marginBottom: 10, flexDirection: isMobile ? 'column' : 'row' }}>
      <div>
        <div style={EYEBROW}>{t('pages.products.options.title')}</div>
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
          {editable ? t('pages.products.options.subtitleEditable') : t('pages.products.options.subtitleReadOnly', { source: sourceName ?? '—' })}
        </div>
      </div>
      {data && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
            {t('pages.products.options.totalSummary', { total: data.totalAvailableQty.toLocaleString(locale), count: options.length })}
          </span>
          {sourceName && <Pill tone={editable ? 'brand' : 'neutral'} dot size="sm">{t('pages.products.options.stockSource', { source: sourceName })}</Pill>}
        </div>
      )}
    </div>
  )

  if (loading && !data) {
    return (
      <div style={{ marginTop: 28 }}>
        <div style={{ ...EYEBROW, marginBottom: 10 }}>{t('pages.products.options.title')}</div>
        <div style={{ padding: '14px 16px', borderRadius: 10, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon size={16}>{I.refresh}</Icon>
          {t('pages.products.options.loadingDetail')}
        </div>
      </div>
    )
  }

  if (loadError !== null && !data) {
    return (
      <div style={{ marginTop: 28 }}>
        <div style={{ ...EYEBROW, marginBottom: 10 }}>{t('pages.products.options.title')}</div>
        <div style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid var(--danger-100)', color: 'var(--danger)', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Icon size={16}>{I.alert}</Icon>
          <span style={{ flex: 1, minWidth: 0 }}>{t('pages.products.options.loadErrorDetail')}</span>
          <Button kind="secondary" size="xs" icon={I.refresh} onClick={() => setReload(value => value + 1)}>{t('pages.products.options.retry')}</Button>
        </div>
      </div>
    )
  }

  const optionName = (option: ProductOption) => (
    <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
        <span style={{ fontWeight: 800, color: 'var(--fg)', fontSize: 13, ...TEXT_WRAP_STYLE }}>{option.optionLabel ?? option.name}</span>
        <Pill tone={option.status === 'ACTIVE' ? 'success' : option.status === 'DRAFT' ? 'warning' : 'neutral'} dot size="sm">{t(`pages.products.status.${option.status}`)}</Pill>
      </div>
      <OptionLinkHint variantId={optionListing(option)?.externalVariantId} />
    </div>
  )

  const quantityCell = (row: ProductStockRow) => (
    <div style={{ display: 'grid', gap: 2, justifyItems: isMobile ? 'start' : 'end' }}>
      <span style={{ fontWeight: 800, color: 'var(--fg)' }}>{row.onHandQty.toLocaleString(locale)}</span>
      <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>
        {t('pages.products.options.committed', { count: row.committedQty, quantity: row.committedQty.toLocaleString(locale) })}
      </span>
    </div>
  )

  const actions = (option: ProductOption, editing: boolean) => {
    if ((!editable && !statusEditable) || editing) return null
    if (editable && option.status === 'ACTIVE' && sessionPush[option.id]?.kind === 'failed') {
      return <Button kind="secondary" size="xs" onClick={() => retryPush(option)} style={mobileButtonStyle(isMobile)}>{t('pages.products.options.retryPush')}</Button>
    }
    return <Button kind="secondary" size="xs" onClick={() => beginEdit(option)} disabled={editingId !== null} style={mobileButtonStyle(isMobile)}>{t('pages.productDetail.stock.actions.edit')}</Button>
  }

  const editor = (option: ProductOption) => (
    <form
      aria-label={t('pages.products.options.editOption', { option: option.optionLabel ?? option.name })}
      onSubmit={event => { event.preventDefault(); if (!saving) void saveEdit(option) }}
      style={{ display: 'grid', gap: 'var(--sp-4)', padding: isMobile ? 'var(--sp-4)' : 'var(--sp-6)', background: 'var(--ink-50)', borderTop: '1px solid var(--border-subtle)', borderRadius: isMobile ? 'var(--r-sm)' : undefined, minWidth: 0 }}
    >
      <div style={{ fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-bold)', color: 'var(--brand-900)', ...TEXT_WRAP_STYLE }}>
        {t('pages.products.options.editOption', { option: option.optionLabel ?? option.name })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(auto-fit, minmax(180px, 240px))', gap: isMobile ? 'var(--sp-4)' : 'var(--sp-6)', alignItems: 'start', minWidth: 0 }}>
        {statusEditable && (
          <label style={{ minWidth: 0 }}>
            <div style={{ fontSize: 'var(--fs-13)', fontWeight: 'var(--fw-semibold)', color: 'var(--fg)', marginBottom: 6 }}>{t('pages.productForm.fields.status')}</div>
            <select
              aria-label={t('pages.products.options.statusFor', { option: option.optionLabel ?? option.name })}
              value={draftStatus}
              disabled={saving}
              autoFocus
              onChange={event => setDraftStatus(event.target.value as ProductStatus)}
              style={{ font: 'inherit', fontSize: 'var(--fs-14)', color: 'var(--fg)', background: 'var(--white)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '11px 14px', width: '100%' }}
            >
              {(['ACTIVE', 'DRAFT', 'INACTIVE'] as const).map(status => <option key={status} value={status}>{t(`pages.products.status.${status}`)}</option>)}
            </select>
          </label>
        )}
        {editable && option.stock.map(row => (
          <Field
            key={row.warehouseId}
            label={row.warehouseName}
            type="number"
            value={drafts[row.warehouseId] ?? ''}
            disabled={saving}
            onChange={event => setDrafts(prev => ({ ...prev, [row.warehouseId]: event.target.value }))}
            hint={Number(drafts[row.warehouseId]) !== row.onHandQty
              ? t('pages.products.options.wasCommitted', { was: row.onHandQty.toLocaleString(locale), committed: row.committedQty.toLocaleString(locale) })
              : t('pages.products.options.committed', { count: row.committedQty, quantity: row.committedQty.toLocaleString(locale) })}
            style={{ minWidth: 0 }}
          />
        ))}
      </div>
      {statusEditable && <div style={{ fontSize: 'var(--fs-13)', lineHeight: '20px', color: 'var(--fg-muted)' }}>{t('pages.products.options.activationHint')}</div>}
      {rowError !== null && errorMessage}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
        <Button kind="secondary" onClick={cancelEdit} disabled={saving} style={mobileButtonStyle(isMobile)}>{t('pages.productDetail.stock.actions.cancel')}</Button>
        <Button kind="primary" type="submit" disabled={saving} style={mobileButtonStyle(isMobile)}>{t('pages.products.options.saveChanges')}</Button>
      </div>
    </form>
  )

  const errorMessage = (
    <div role="alert" style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid var(--danger-100)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
      <Icon size={15}>{I.alert}</Icon>
      {String(t(stockErrorKey(rowError), { defaultValue: String(t('pages.productDetail.stock.errors.generic')) }))}
    </div>
  )

  return (
    <div style={{ marginTop: 28 }}>
      {header}

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 14px', borderRadius: 10, background: 'var(--ink-50)', marginBottom: 12 }}>
        <Icon size={15} color="var(--fg-subtle)" style={{ marginTop: 1, flexShrink: 0 }}>{I.link}</Icon>
        <span style={{ flex: 1, fontSize: 12.5, lineHeight: '18px', color: 'var(--fg-muted)' }}>{t('pages.products.options.note')}</span>
      </div>

      {editable && parentRows.length > 0 && (
        <div style={{ display: 'grid', gap: 12, padding: '14px 16px', borderRadius: 10, background: 'var(--warning-100)', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Icon size={16} color="var(--warning-700)" style={{ flexShrink: 0 }}>{I.alert}</Icon>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 700, lineHeight: '19px', color: 'var(--warning-700)' }}>{t('pages.products.options.parentStock.warning')}</span>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', paddingInlineStart: 26 }}>
            {parentRows.map(row => (
              <div key={row.warehouseId} style={{ display: 'grid' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{row.warehouseName} · {row.onHandQty.toLocaleString(locale)}</span>
                <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{t('pages.products.options.parentStock.notSynced')}</span>
              </div>
            ))}
          </div>
          <div style={{ paddingInlineStart: isMobile ? 0 : 26 }}>
            <Button kind="secondary" size="xs" onClick={clearParentStock} disabled={clearingParent} style={mobileButtonStyle(isMobile)}>
              {t('pages.products.options.parentStock.setToZero')}
            </Button>
          </div>
        </div>
      )}

      {rowError !== null && editingId === null && <div style={{ marginBottom: 12 }}>{errorMessage}</div>}

      {isMobile ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {options.map(option => {
            const editing = editingId === option.id
            return (
              <div key={option.id} style={{ display: 'grid', gap: 12, padding: 14, borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'var(--white)', minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {optionName(option)}
                  <SyncCell sync={optionSyncState(option, editable, t, locale)} session={sessionPush[option.id]} option={option} locale={locale} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 10 }}>
                  {option.stock.map(row => (
                    <div key={row.warehouseId} style={{ minWidth: 0 }}>
                      <div style={{ ...TABLE_HEADER, padding: 0, ...TEXT_WRAP_STYLE }}>{row.warehouseName}</div>
                      <div style={{ marginTop: 4 }}>{quantityCell(row)}</div>
                    </div>
                  ))}
                  <div>
                    <div style={{ ...TABLE_HEADER, padding: 0 }}>{t('pages.productDetail.stock.headers.available')}</div>
                    <div style={{ marginTop: 4, fontWeight: 800, color: option.availableQty === 0 ? 'var(--danger-700)' : 'var(--fg)' }}>{option.availableQty.toLocaleString(locale)}</div>
                  </div>
                </div>
                {actions(option, editing)}
                {editing && editor(option)}
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: '1px solid var(--border-subtle)', overflowX: 'auto', background: 'white' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ink-50)' }}>
                <th style={{ ...TABLE_HEADER, textAlign: 'start' }}>{t('pages.products.options.headers.option')}</th>
                {warehouses.map(row => (
                  <th key={row.warehouseId} style={{ ...TABLE_HEADER, textAlign: 'end' }}>
                    <div>{row.warehouseName}</div>
                    <div style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none', marginTop: 2 }}>{t('pages.products.options.headers.onHandCode', { code: row.warehouseCode })}</div>
                  </th>
                ))}
                <th style={{ ...TABLE_HEADER, textAlign: 'end' }}>{t('pages.productDetail.stock.headers.available')}</th>
                <th style={{ ...TABLE_HEADER, textAlign: 'start' }}>{editable ? t('pages.products.options.headers.salla') : t('pages.products.options.headers.sallaSource')}</th>
                {(editable || statusEditable) && <th style={TABLE_HEADER} />}
              </tr>
            </thead>
            <tbody>
              {options.map(option => {
                const editing = editingId === option.id
                return (
                  <Fragment key={option.id}>
                    <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 16px' }}>{optionName(option)}</td>
                      {option.stock.map(row => (
                        <td key={row.warehouseId} style={{ padding: '12px 16px', textAlign: 'end' }}>{quantityCell(row)}</td>
                      ))}
                      <td style={{ padding: '12px 16px', textAlign: 'end', fontWeight: 800, color: option.availableQty === 0 ? 'var(--danger-700)' : 'var(--fg)' }}>
                        {option.availableQty.toLocaleString(locale)}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <SyncCell sync={optionSyncState(option, editable, t, locale)} session={sessionPush[option.id]} option={option} locale={locale} />
                      </td>
                      {(editable || statusEditable) && <td style={{ padding: '12px 16px', textAlign: 'end' }}>{actions(option, editing)}</td>}
                    </tr>
                    {editing && <tr><td colSpan={warehouses.length + 4} style={{ padding: 0 }}>{editor(option)}</td></tr>}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
